import apiClient from "./middleware";

/**
 * Full checkup report for a single channel (mirrors the server response).
 *
 * Sourced from Firestore: channelSettings + channelLastUpdates (live),
 * channelCheckupExtras (synced daily from YouTube + ClickHouse),
 * channelCheckupPlaylists (synced weekly from YouTube), and
 * channelCheckupRecentUploads (synced daily from ClickHouse — see
 * docs/channel-checkup/README.md).
 */
export interface ChannelCheckupReport {
  channelId: string;
  channelTitle: string | null;
  channelUrl: string;

  // from channelSettings
  bannerUrl: string | null;
  defaultBannerUrl: string | null;
  bannerState: string | null;
  lastBannerChangeAt: string | null;
  channelType: string | null;
  ownerShortName: string | null;
  brand: string | null;
  language: string | null;
  contentOwnerId: string | null;
  channelManagers: string[];
  channelManagerEmails: string[];
  inactive: boolean;
  status: string | null;

  // last published/upload: extras ClickHouse, else OAC BQ snapshot
  lastUpdatesAvailable: boolean;
  lastPublishedAt: string | null;
  lastUploadAt: string | null;
  lastScheduledAtAny: string | null;
  lastScheduledThisMonth: string | null;

  // from channelCheckupExtras (daily sync: YouTube + ClickHouse)
  /** Publish date of the non-subscriber channel trailer. */
  featuredVideoDate: string | null;
  /** When featuredVideoDate last refreshed successfully (null = unknown / pre-tracking). */
  featuredVideoSyncedAt: string | null;
  /** When the ClickHouse dates (scheduled / last published / upload) last refreshed successfully. */
  videoDatesSyncedAt: string | null;
  maxScheduledVideo: string | null;
  maxScheduledShort: string | null;
  operationStatus: string | null;

  // from channelCheckupPlaylists (weekly sync: YouTube)
  playlistCount: number;
  playlists: Array<{
    playlistId: string;
    title: string;
    /** When the most recently added item was added to the playlist. */
    lastItemAddedAt: string | null;
    /** Publish date of the newest video in the playlist (null until the next playlist sync). */
    newestVideoPublishedAt: string | null;
  }>;

  // from channelCheckupRecentUploads (daily sync: ClickHouse)
  recentUploadCount: number;
  recentUploads: Array<{
    videoId: string;
    title: string;
    videoType: string;
    privacyStatus: string;
    uploadedAt: string | null;
    publishedAt: string | null;
    thumbnailUrl: string;
    videoUrl: string;
    description: string | null;
    tags: string[];
  }>;

  /** Relative path to the Metadata Audit Title QC page. */
  qcPath: string;
  /** Pending/open Title QC issues for this channel (`is_active_issue`, not accepted/rejected). */
  qcOpenIssueCount: number;
}

/** When a snapshot behind the report was last written, plus its non-fatal sync warnings. */
export interface ChannelCheckupSyncSource {
  updatedAt: string | null;
  warnings: string[];
}

export interface ChannelCheckupSyncStatus {
  /** Featured, scheduled, last published/upload, operation status (twice daily). */
  extras: ChannelCheckupSyncSource;
  /** Playlist inventory (twice weekly, active channels only). */
  playlists: ChannelCheckupSyncSource;
  /** Recent uploads (written by the extras job). */
  recentUploads: ChannelCheckupSyncSource;
  /** BigQuery last-updates view fallback for last published/upload. */
  lastUpdates: ChannelCheckupSyncSource;
  /** Channel managers / type / brand / inactive from BigQuery (daily). */
  channelSettings: ChannelCheckupSyncSource;
}

export interface ChannelCheckupListResult {
  reports: ChannelCheckupReport[];
  syncStatus: ChannelCheckupSyncStatus;
}

const emptySyncSource = (): ChannelCheckupSyncSource => ({
  updatedAt: null,
  warnings: [],
});

export const emptySyncStatus = (): ChannelCheckupSyncStatus => ({
  extras: emptySyncSource(),
  playlists: emptySyncSource(),
  recentUploads: emptySyncSource(),
  lastUpdates: emptySyncSource(),
  channelSettings: emptySyncSource(),
});

/**
 * Manager labels for display/filtering: names when the source has them,
 * otherwise emails. The server drops a legacy name it cannot tie to the
 * current manager emails, so an email-only row means "reassigned, name
 * column not yet updated" rather than "no manager".
 */
export function channelManagerLabels(
  r: Pick<ChannelCheckupReport, "channelManagers" | "channelManagerEmails">
): string[] {
  if (r.channelManagers.length > 0) return r.channelManagers;
  return r.channelManagerEmails ?? [];
}

/** True when status is explicitly Private or Unlisted (any case). Empty/missing is kept. */
export function isExplicitlyNonPublicPrivacy(
  status: string | undefined
): boolean {
  const normalized = (status ?? "").trim().toLowerCase();
  return normalized === "private" || normalized === "unlisted";
}

export function keepPublicRecentUploads<T extends { privacyStatus?: string }>(
  uploads: T[]
): T[] {
  return uploads
    .filter((u) => !isExplicitlyNonPublicPrivacy(u.privacyStatus))
    .slice(0, 20);
}

export const channelCheckupApi = {
  /**
   * Load a Firestore-backed checkup report for every channel in channelSettings.
   * Used to populate the channel selector and the filterable table.
   * Recent uploads are public-only so leftover Private/Unlisted Firestore rows never display.
   */
  listChannels: async (): Promise<ChannelCheckupListResult> => {
    const response = await apiClient.get<{
      reports: ChannelCheckupReport[];
      count: number;
      syncStatus?: Partial<ChannelCheckupSyncStatus>;
    }>("/channel-checkup/channels");
    const reports = (response.data.reports ?? []).map((report) => {
      const recentUploads = keepPublicRecentUploads(
        report.recentUploads ?? []
      );
      return {
        ...report,
        channelManagers: report.channelManagers ?? [],
        channelManagerEmails: report.channelManagerEmails ?? [],
        featuredVideoSyncedAt: report.featuredVideoSyncedAt ?? null,
        videoDatesSyncedAt: report.videoDatesSyncedAt ?? null,
        playlists: (report.playlists ?? []).map((pl) => ({
          ...pl,
          newestVideoPublishedAt: pl.newestVideoPublishedAt ?? null,
        })),
        recentUploads,
        recentUploadCount: recentUploads.length,
      };
    });
    const syncStatus: ChannelCheckupSyncStatus = {
      ...emptySyncStatus(),
      ...(response.data.syncStatus ?? {}),
    };
    return { reports, syncStatus };
  },
};
